import { useEffect, useRef, useState } from "react";

import { Alert, Box, Snackbar, Stack } from "@mui/material";
import { Typography, UploadCloud02Icon, Button, Trash01Icon } from "components";
import { base, gray, primary } from "configs/colors";
import { shadows } from "configs/shadows";

interface Props {
  fileExtensions?: Array<string>;
  fileSize?: number; // MB
  description?: string;
  onChange?: (file: File | null) => void;
}

const Upload: React.FC<Props> = ({
  fileExtensions,
  fileSize,
  description,
  onChange,
}) => {
  const containerRef = useRef<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const uploadErrMsg = useRef<string>("");
  const [openAlert, setOpenAlert] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const handleCloseAlert = () => {
    setOpenAlert(false);
  };

  const handleFileClear = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setUploadedFile(null);
    onChange?.(null);
  };

  const handleFinderOpen = () => {
    const inputTarget = inputRef.current;
    if (!inputTarget || uploadedFile) return;
    inputTarget.click();
  };

  const fileHandler = (file: File) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      const size = file.size / 1048576;
      if (typeof fileSize === "number" && size > fileSize) {
        uploadErrMsg.current = `Incorrect file size. It must be less than or equal to ${fileSize}MB`;
        setOpenAlert(false);
        handleFileClear();
        return;
      }
      onChange?.(file);
      setUploadedFile(file);
    };
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    fileHandler(file);
  };

  useEffect(
    function clearUploadErrMsg() {
      if (openAlert) return;
      uploadErrMsg.current = "";
    },
    [openAlert]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const dragOverEventHandler = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const dropEventHandler = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const file = e.dataTransfer?.files?.[0];
      if (!file) return;
      fileHandler(file);
    };

    container.addEventListener("dragover", dragOverEventHandler, false);
    container.addEventListener("drop", dropEventHandler, false);

    // eslint-disable-next-line consistent-return
    return () => {
      container.removeEventListener("dragover", dragOverEventHandler, false);
      container.removeEventListener("drop", dropEventHandler, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box
        ref={containerRef}
        padding="24px"
        borderRadius="12px"
        border={`1px dashed ${gray[300]}`}
        textAlign="center"
        sx={{
          background: base.White,
          cursor: "pointer",
          transition: "opacity .3s",
          "&:hover": uploadedFile ? undefined : { opacity: 0.7 },
        }}
        onClick={handleFinderOpen}
        draggable
      >
        {uploadedFile ? (
          <Stack direction="row" alignItems="center" justifyContent="center">
            <Typography fontWeight={600} type="TextMD" color="brown.600">
              {uploadedFile?.name}
            </Typography>
            <Button
              variant="text"
              size="icon-sm"
              color="error"
              onClick={handleFileClear}
            >
              <Trash01Icon />
            </Button>
          </Stack>
        ) : (
          <>
            <Box
              display="inline-flex"
              padding="8px"
              marginBottom="16px"
              borderRadius="8px"
              border={`1px solid ${gray[300]}`}
              boxShadow={shadows.xs}
              sx={{ background: base.White }}
            >
              <UploadCloud02Icon fontSize="medium" sx={{ color: gray[600] }} />
            </Box>
            <Typography type="TextSM" color={gray[600]} textAlign="center">
              <span style={{ color: primary[700], fontWeight: 600 }}>
                Click to upload
              </span>
              &nbsp;or drag and drop
            </Typography>
            <Typography type="TextXS" color={gray[600]} textAlign="center">
              {description}
            </Typography>
            <input
              style={{ display: "none" }}
              ref={inputRef}
              type="file"
              accept={fileExtensions ? fileExtensions.join(", ") : undefined}
              onChange={handleFileChange}
            />
          </>
        )}
      </Box>

      <Snackbar
        open={openAlert}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={handleCloseAlert}
      >
        <Alert
          onClose={handleCloseAlert}
          severity="error"
          sx={{ width: "100%" }}
        >
          {uploadErrMsg.current}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Upload;
